import React, { forwardRef } from 'react';
import Type from 'prop-types';
import { HashLink } from 'react-router-hash-link';
import { noop, groupBy } from 'lodash';
import { AppBar as MuiAppBar, Toolbar, Typography, IconButton, Badge } from '@material-ui/core';
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons';
import { ru } from 'locale';
import ConditionalWrap from '../ConditionalWrap';
import HideOnScroll from '../HideOnScroll';
import OverflowMenu from '../OverflowMenu';
import useStyles from './AppBar.styles';

const AppBar = forwardRef(function AppBar(props, ref) {
  const classes = useStyles();

  const {
    title: titleProp,
    subtitle: subtitleProp,
    navButtonAriaLabel,
    navIcon: Icon,
    onNavButtonClick,
    children,
    hideOnScroll,
    additionalSlot,
    actions,
    activeActionId,
    onActionSelected,
    overflowMenuAnchorEl,
    onOverflowMenuOpen,
    onOverflowMenuClose,
    typeNavIcon,
    backUrl,
    ...other
  } = props;

  let title = titleProp;
  let subtitle = subtitleProp;

  if (title !== null) {
    title = (
      <Typography
        variant={subtitle !== null ? 'body1' : 'h6'}
        component="span"
        className={classes.title}
      >
        {title}
      </Typography>
    );
  }

  if (subtitle !== null) {
    subtitle = <Typography variant="body2">{subtitle}</Typography>;
  }

  const { always: appBarActions, never: overflowMenuActions } = groupBy(
    actions,
    action => action.show,
  );

  const handleActionSelected = actionId => () => {
    onActionSelected(actionId);
  };

  const renderNavigationIcon = () => {
    switch (typeNavIcon) {
      case 'link':
        return (
          <IconButton
            edge="start"
            color="inherit"
            className={classes.navButton}
            aria-label={navButtonAriaLabel}
            component={HashLink}
            to={backUrl}
          >
            <Icon />
          </IconButton>
        );

      case 'normal':
      default:
        return (
          <IconButton
            edge="start"
            color="inherit"
            className={classes.navButton}
            aria-label={navButtonAriaLabel}
            onClick={onNavButtonClick}
          >
            <Icon />
          </IconButton>
        );
    }
  };

  const renderAppBarActions = () => {
    return appBarActions?.map(action => {
      const opacity = action.id === activeActionId ? 1 : 0.5;
      const iconButtonStyles = activeActionId !== null ? { opacity } : {};
      const IconAction = action.icon;

      return (
        <IconButton
          key={action.title}
          edge="end"
          className={classes.actionButton}
          color="inherit"
          aria-label={action.title}
          style={iconButtonStyles}
          onClick={handleActionSelected(action.id)}
        >
          {action?.badge ? (
            <Badge {...action.badge}>
              <IconAction />
            </Badge>
          ) : (
            <IconAction />
          )}
        </IconButton>
      );
    });
  };

  return (
    <ConditionalWrap
      condition={hideOnScroll}
      wrap={childrens => <HideOnScroll>{childrens}</HideOnScroll>}
    >
      <MuiAppBar ref={ref} {...other}>
        <Toolbar>
          {renderNavigationIcon()}
          <div className={classes.content}>
            {title}
            {subtitle}
          </div>
          {renderAppBarActions()}

          {overflowMenuActions?.length > 0 && (
            <OverflowMenu
              actions={overflowMenuActions}
              anchorEl={overflowMenuAnchorEl}
              onOpen={onOverflowMenuOpen}
              onClose={onOverflowMenuClose}
              onActionSelected={onActionSelected}
            />
          )}

          {children}
        </Toolbar>

        {additionalSlot}
      </MuiAppBar>
    </ConditionalWrap>
  );
});

AppBar.propTypes = {
  title: Type.string,
  subtitle: Type.string,
  position: Type.oneOf(['absolute', 'fixed', 'relative', 'static', 'sticky']),
  navButtonAriaLabel: Type.string,
  navIcon: Type.elementType,
  actions: Type.arrayOf(
    Type.shape({
      id: Type.string.isRequired,
      title: Type.string.isRequired,
      show: Type.oneOf(['always', 'never']).isRequired,
      icon: Type.elementType,
      badge: Type.shape({
        badgeContent: Type.node.isRequired,
      }),
    }),
  ),
  activeActionId: Type.oneOfType([Type.string, Type.number]),
  children: Type.node,
  elevation: Type.number,
  hideOnScroll: Type.bool,
  additionalSlot: Type.node,
  overflowMenuAnchorEl: Type.instanceOf(Element),
  onNavButtonClick: Type.func,
  onActionSelected: Type.func,
  onOverflowMenuOpen: Type.func,
  onOverflowMenuClose: Type.func,
  typeNavIcon: Type.oneOf(['normal', 'link']),
  backUrl: Type.string,
};

AppBar.defaultProps = {
  position: 'sticky',
  title: null,
  subtitle: null,
  actions: [],
  activeActionId: null,
  elevation: 1,
  hideOnScroll: false,
  children: null,
  additionalSlot: null,
  navButtonAriaLabel: ru.back,
  navIcon: ArrowBackIcon,
  overflowMenuAnchorEl: null,
  onNavButtonClick: noop,
  onActionSelected: noop,
  onOverflowMenuOpen: noop,
  onOverflowMenuClose: noop,
  typeNavIcon: 'normal',
  backUrl: '',
};

export default AppBar;
